import * as React from "react";
import { useState } from "react";
import axios from "axios";
import moment from "moment";
import { Button, CircularProgress, TableCell, TableRow } from "@material-ui/core";
import GetAppIcon from "@material-ui/icons/GetApp";
import { Notification, useNotify } from "react-admin";
import { ENDPOINTS } from "../apiConstants";
import { getConfigGet } from "../getConfig";
import { BrandPicker, brandLabel, useBrandNames } from "./brands";
import { downloadCsv } from "./csv";
import {
  DataTable,
  DateField,
  FilterSelect,
  LocalPagination,
  PageHeader,
  SectionHeader,
  StatusChip,
  errorMessage,
  fmtDateTime,
  gbp,
  monthOptions,
  previousMonth,
  useCloStyles,
} from "./shared";

const CUSTOM = "custom";

const ROW_PAGE_SIZES = [50, 100, 250];

const CSV_COLUMNS = [
  "id",
  "transaction_at",
  "network",
  "event_type",
  "arn",
  "original_arn",
  "amount_pence",
  "currency",
  "status",
  "reason",
  "campaign_id",
  "user_id",
  "matched_at",
];

const TOTAL_COLUMNS = [
  { label: "Type" },
  { label: "Outcome" },
  { label: "Count", numeric: true },
  { label: "Amount", numeric: true },
];

const ROW_COLUMNS = [
  { label: "ID" },
  { label: "Transaction at" },
  { label: "Network" },
  { label: "Event" },
  { label: "ARN" },
  { label: "Original ARN" },
  { label: "Amount", numeric: true },
  { label: "Status" },
  { label: "Reason" },
  { label: "Campaign" },
  { label: "User ID" },
  { label: "Matched at" },
];

const utcDay = (date: string, addDays = 0): string =>
  moment.utc(date, "YYYY-MM-DD").add(addDays, "days").toISOString();

const Reconciliation = () => {
  const classes = useCloStyles();
  const notify = useNotify();
  const [brandId, setBrandId] = useState("");
  const [period, setPeriod] = useState(previousMonth());
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [rowPage, setRowPage] = useState(0);
  const [rowPageSize, setRowPageSize] = useState(ROW_PAGE_SIZES[0]);
  const names = useBrandNames([brandId, result?.brandId]);

  const range = (): { from: string; to: string } | null => {
    if (period !== CUSTOM) {
      const start = moment.utc(period, "YYYY-MM").startOf("month");
      return { from: start.toISOString(), to: start.clone().add(1, "month").toISOString() };
    }
    if (!fromDate || !toDate) return null;
    return { from: utcDay(fromDate), to: utcDay(toDate, 1) };
  };

  const run = async () => {
    if (!brandId) {
      notify("Pick a brand", "warning");
      return;
    }
    const r = range();
    if (!r) {
      notify("Pick both a start and end date", "warning");
      return;
    }
    if (r.from >= r.to) {
      notify("Start date must be on or before end date", "warning");
      return;
    }
    try {
      setLoading(true);
      const res: any = await axios.get(
        ENDPOINTS.CLO_RECONCILIATION,
        getConfigGet({ brand_id: brandId, from: r.from, to: r.to })
      );
      setResult(res.data);
      setRowPage(0);
    } catch (err) {
      setResult(null);
      notify(errorMessage(err, "Failed to load reconciliation"), "warning");
    } finally {
      setLoading(false);
    }
  };

  const rows: any[] = result?.rows || [];

  const exportCsv = () => {
    const stamp = `${result.brandId}-${String(result.from).slice(0, 10)}-${String(result.to).slice(0, 10)}`;
    downloadCsv(`clo-reconciliation-${stamp}.csv`, CSV_COLUMNS, rows);
  };

  const totalsByKey: Record<string, any> = result?.totals || {};
  const totals = Object.keys(totalsByKey).map((key) => {
    const [type, outcome] = key.split(":");
    return { key, type, outcome, count: totalsByKey[key].count, amountPence: totalsByKey[key].amountPence };
  });
  const pageRows = rows.slice(rowPage * rowPageSize, (rowPage + 1) * rowPageSize);
  const periodOptions = [...monthOptions(), { value: CUSTOM, label: "Custom range…" }];

  return (
    <div className={classes.main}>
      <PageHeader
        title="Reconciliation"
        subtitle="Every network event for one retailer over a period, with totals by type and outcome"
        actions={
          result && (
            <Button variant="outlined" color="primary" startIcon={<GetAppIcon />} onClick={exportCsv} disabled={rows.length === 0}>
              Download CSV
            </Button>
          )
        }
      />

      <div className={classes.filterContainer}>
        <BrandPicker className={classes.filterField} value={brandId} onChange={setBrandId} required />
        <FilterSelect label="Period" value={period} onChange={setPeriod} options={periodOptions} allLabel={null} />
        {period === CUSTOM && (
          <>
            <DateField label="From" value={fromDate} onChange={setFromDate} className={classes.filterField} />
            <DateField label="To" value={toDate} onChange={setToDate} className={classes.filterField} />
          </>
        )}
        <Button className={classes.filterButton} variant="contained" color="primary" disableElevation onClick={run} disabled={loading}>
          Run
        </Button>
      </div>

      {loading && <div className={classes.loadingContainer}><CircularProgress size={40} /></div>}

      {!loading && result && (
        <>
          <SectionHeader
            title="Totals"
            meta={`${brandLabel(names, result.brandId)} · ${moment.utc(result.from).format("D MMM YYYY")} to ${moment
              .utc(result.to)
              .subtract(1, "ms")
              .format("D MMM YYYY")}`}
          />
          <DataTable columns={TOTAL_COLUMNS} isEmpty={totals.length === 0} empty="No transactions in this period.">
            {totals.map((t) => (
              <TableRow key={t.key} hover>
                <TableCell>{t.type}</TableCell>
                <TableCell><StatusChip status={t.outcome} /></TableCell>
                <TableCell align="right">{t.count}</TableCell>
                <TableCell align="right">{gbp(t.amountPence)}</TableCell>
              </TableRow>
            ))}
          </DataTable>

          <SectionHeader title="Transactions" meta={`${rows.length} rows`} />
          <DataTable
            columns={ROW_COLUMNS}
            isEmpty={rows.length === 0}
            empty="No transactions in this period."
            footer={
              rows.length > ROW_PAGE_SIZES[0] ? (
                <LocalPagination
                  count={rows.length}
                  page={rowPage}
                  pageSize={rowPageSize}
                  sizes={ROW_PAGE_SIZES}
                  onPage={setRowPage}
                  onPageSize={(size) => {
                    setRowPageSize(size);
                    setRowPage(0);
                  }}
                />
              ) : undefined
            }
          >
            {pageRows.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>{r.id}</TableCell>
                <TableCell>{fmtDateTime(r.transaction_at)}</TableCell>
                <TableCell>{r.network}</TableCell>
                <TableCell>{r.event_type}</TableCell>
                <TableCell>{r.arn || "—"}</TableCell>
                <TableCell>{r.original_arn || "—"}</TableCell>
                <TableCell align="right">{gbp(r.amount_pence)}</TableCell>
                <TableCell><StatusChip status={r.status} /></TableCell>
                <TableCell>{r.reason || "—"}</TableCell>
                <TableCell>{r.campaign_id ?? "—"}</TableCell>
                <TableCell>{r.user_id ?? "—"}</TableCell>
                <TableCell>{fmtDateTime(r.matched_at)}</TableCell>
              </TableRow>
            ))}
          </DataTable>
        </>
      )}
      <Notification />
    </div>
  );
};

export default Reconciliation;
